import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface UserConnectionProfile {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
  academy?: string;
}

export function useUserConnections(userId: string | undefined) {
  const [connections, setConnections] = useState<UserConnectionProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    // Fetch the user_ids of the target user's connections
    const { data, error } = await supabase.rpc('get_user_connections', { target_user: userId });
    if (error) {
      setError('Error fetching user connections');
      setLoading(false);
      return;
    }
    const ids = (data || []).map((r: any) => r.user_id).filter(Boolean);
    if (ids.length === 0) {
      setConnections([]);
      setLoading(false);
      return;
    }
    // Fetch the complete profiles of the connections
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('id, full_name, username, profile_picture_url, belt_rank, main_academy:main_academy_id(name)')
      .in('id', ids);
    if (profileError) {
      setError('Error fetching connection profiles.');
      setLoading(false);
      return;
    }
    const connectionsWithAcademy = (profiles || []).map((p: any) => ({
      ...p,
      academy: p.main_academy ? p.main_academy.name : undefined,
    }));
    setConnections(connectionsWithAcademy);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  return { connections, loading, error, refetch: fetchConnections };
} 